import { useState, useEffect, useMemo } from "react";
import { useTMLogContext } from "../../../../../context/domain/operations/TMLogContext";
import {
  ClipboardCopy,
  Trash,
  X,
  ChevronUp,
  ChevronDown,
} from "../../../../../components/icons/svg/outline";
import {
  Button,
  TooltipArrow,
  SearchableDetailsDropdown,
} from "../../../../../components/ui";
import { LINE_ITEM_TYPES, ADMIN_LINKS, COLUMN_CONFIG } from "./constants";
import {
  createItemDataFromOption,
  calculateLineItemTotal,
  formatSearchableDetailsDropdownOptions,
} from "./helpers";
import { useReplaceOnFirstType } from "../../../../../utils/hooks/useReplaceOnFirstType";
import "./TMLineItem.css";

/**
 * TMLineItem Component
 * Displays a single line item row in the Time & Materials table
 * Handles Labor, Material, Equipment, Subcontractors, Concrete, and Custom types
 *
 * Column visibility by type:
 * - Labor: RT Hours, OT Hours, Unit, Price/Unit (RT), Price/Unit (OT)
 * - Material: Quantity, Unit, Price/Unit (RT only)
 * - Concrete: Quantity, Unit, Price/Unit (RT only)
 * - Equipment: Quantity, RT Hours, OT Hours, Unit, Price/Unit (RT), Price/Unit (OT)
 * - Subcontractor: Quantity, Unit, Price/Unit (RT only)
 *
 * @param {Object} item Line item (`type`, quantities, rates, option fields).
 * @param {Function} [onAddItems] Callback when multi-select creates extra items.
 * @param {Function} [onChange] Called with the updated item.
 * @param {Function} [onDuplicate] Duplicate this row.
 * @param {Function} [onRemove] Remove this row.
 * @param {*} [loadedFromReport] Whether the report was loaded from the API.
 * @param {boolean} [showPricing=false] Show pricing columns (Price/Unit RT, Price/Unit OT, Total).
 * @param {boolean} [readOnly=false] Disables all interactions and hides action buttons.
 * @returns {JSX.Element}
 */
const TMLineItem = ({
  item,
  onAddItems,
  onChange,
  onDuplicate,
  onRemove,
  loadedFromReport,
  showPricing = false,
  readOnly = false,
}) => {
  const { workers } = useTMLogContext();
  const typeConfig = LINE_ITEM_TYPES[item.type] || LINE_ITEM_TYPES.labor;
  const [options, setOptions] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);

  const cols = COLUMN_CONFIG[item.type] || COLUMN_CONFIG.labor;

  // Initialize replace-on-first-type hooks for each number input
  const quantityInput = useReplaceOnFirstType();
  const rtHoursInput = useReplaceOnFirstType();
  const otHoursInput = useReplaceOnFirstType();
  const rateRtInput = useReplaceOnFirstType();
  const rateOtInput = useReplaceOnFirstType();

  // Fetch options and units
  useEffect(() => {
    /**
     * Load dropdown options (workers from context for labor) and units for this type.
     *
     * @returns {Promise<void>}
     */
    const fetchData = async () => {
      setLoading(true);
      try {
        // For labor type, use workers from context instead of fetching
        if (item.type === 'labor' && workers) {
          setOptions(workers || []);
        } else if (!typeConfig.isCustom && typeConfig.fetchOptions) {
          // For other types, fetch options as before
          const data = await typeConfig.fetchOptions();
          setOptions(data || []);
        }

        // Fetch units if configured as a function
        if (typeof typeConfig.units === "function") {
          const fetchedUnits = await typeConfig.units();
          setUnits(fetchedUnits || []);
        } else if (Array.isArray(typeConfig.units)) {
          setUnits(typeConfig.units);
        }
      } catch (error) {
        console.error(`Failed to fetch data for ${item.type}:`, error);
        setOptions([]);
        setUnits([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [item.type, typeConfig, workers]);

  /**
   * Update one item field, rounding numeric values to 2 decimals.
   *
   * @param {string} field Field key.
   * @param {*} value Next value.
   * @returns {void}
   */
  const handleFieldChange = (field, value) => {
    // Round numeric values to 2 decimal places for currency fields
    let roundedValue = value;
    if (typeof value === 'number' || (typeof value === 'string' && value !== '' && !isNaN(value))) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        roundedValue = parseFloat(numValue.toFixed(2));
      }
    }
    onChange?.({ ...item, [field]: roundedValue });
  };

  // Simple reusable arrow buttons component
  /**
   * Compact increment/decrement chevrons for a numeric cell.
   *
   * @param {Object} props Component props.
   * @param {Function} props.onIncrement Increment click handler.
   * @param {Function} props.onDecrement Decrement click handler.
   * @returns {JSX.Element}
   */
  const ArrowButtons = ({ onIncrement, onDecrement }) => (
    <div className="flex flex-col border border-l-0 border-gray-300 rounded-r-md shrink-0">
      <button
        type="button"
        onClick={onIncrement}
        className="h-[12px] w-[16px] flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 border-b border-gray-300"
        tabIndex={-1}
      >
        <ChevronUp className="w-2 h-2 text-gray-600" />
      </button>
      <button
        type="button"
        onClick={onDecrement}
        className="h-[12px] w-[16px] flex items-center justify-center hover:bg-gray-100 active:bg-gray-200"
        tabIndex={-1}
      >
        <ChevronDown className="w-2 h-2 text-gray-600" />
      </button>
    </div>
  );

  // Transform options to format expected by SearchableDetailsDropdown
  const dropdownOptions = useMemo(() => {
    return formatSearchableDetailsDropdownOptions(options, item.type);
  }, [options, item.type]);

  // Handle option selection - update both option_id, option_name, and rates
  // Supports both single selection (ID) and multi-selection (array of IDs)
  /**
   * Apply a dropdown selection: single id updates this row; an array can
   * spawn additional items via `onAddItems`.
   *
   * @param {number|string|Array<number|string>} selectedIdOrIds Selected option id(s).
   * @returns {void}
   */
  const handleOptionChange = (selectedIdOrIds) => {
    // Handle multi-select (array of IDs)
    if (Array.isArray(selectedIdOrIds)) {
      // If no selections, clear option fields
      if (selectedIdOrIds.length === 0) {
        onChange?.({
          ...item,
          option_id: null,
          option_name: "",
        });
        return;
      }
      // Handle multi-select case
      const baseId = Date.now();
      const itemsToAdd = selectedIdOrIds.map((selectedId, index) => {
        return {
          ...createItemDataFromOption(
            options.find((opt) => opt.id === selectedId),
            item
          ),
          id: baseId + index, // Generate unique ID for each new item
        };
      }); // Create new items for each selection
      onAddItems?.(itemsToAdd, item.id);

      return; // Exit early after handling multi-select
    }

    // Handle single selection (existing behavior)
    const selectedOption = options.find(
      (opt) => opt.id === parseInt(selectedIdOrIds)
    );

    if (!selectedOption) {
      onChange?.({
        ...item,
        option_id: null,
        option_name: "",
        rate_rt: null,
        rate_ot: null,
      });
      return;
    }

    // Use helper to create item data and update
    const updatedItem = createItemDataFromOption(selectedOption, item);
    onChange?.(updatedItem);
  };

  const total = calculateLineItemTotal(item);

  return (
    <tr className={`${typeConfig.bgColor} border-b text-xs`}>
      {/* Option Column */}
      <td className="px-[1px] md:px-1 py-[2px] caret-black">
        {readOnly ? (
          <div className="text-gray-700 py-[2px] pl-2">
            {item.option_name || "N/A"}
          </div>
        ) : typeConfig.isCustom ? (
          <div className="relative">
            <input
              type="text"
              value={item.option_name || ""}
              onChange={(e) => handleFieldChange("option_name", e.target.value)}
              placeholder="Enter custom name"
              className="w-full h-[24px] px-2 pr-8 py-0 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {item.option_name && (
              <button
                type="button"
                onClick={() => handleFieldChange("option_name", "")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : (
          <SearchableDetailsDropdown
            options={dropdownOptions}
            multiSelect={true}
            value={item.option_id}
            onChange={handleOptionChange}
            placeholder={loading ? "Loading..." : "Select option"}
            disabled={loading}
          />
        )}
      </td>

      {/* Quantity Column */}
      <td className="px-[1px] md:px-1 py-[2px] caret-black">
        {!cols.quantity ? (
          // Empty cell for types that don't use quantity (e.g., Labor)
          <div className="text-gray-400 py-[2px] text-center">-</div>
        ) : readOnly ? (
          <div className="text-gray-700 py-[2px] text-right">
            {item.quantity || "0"}
          </div>
        ) : (
          <div className="flex items-center">
            <input
              type="number"
              inputMode="decimal"
              value={item.quantity || "0"}
              onChange={(e) => handleFieldChange("quantity", e.target.value)}
              onFocus={quantityInput.handleFocus}
              onKeyDown={(e) =>
                quantityInput.handleKeyDown(e, item.quantity, (newValue) =>
                  handleFieldChange("quantity", newValue)
                )
              }
              onBlur={quantityInput.handleBlur}
              className={`h-[24px] w-full text-right pl-1 md:pl-[1px] pr-2 md:pr-2 border border-gray-300 rounded-l-md rounded-r-none focus:outline-none focus:ring-1 focus:ring-blue-500 ${quantityInput.isPristine ? "input-pristine" : ""}`}
              min="0"
              step="any"
            />
            <ArrowButtons
              onIncrement={() => {
                const currentValue = parseFloat(item.quantity) || 0;
                const newValue = parseFloat((currentValue + 0.5).toFixed(2));
                handleFieldChange("quantity", newValue);
              }}
              onDecrement={() => {
                const currentValue = parseFloat(item.quantity) || 0;
                const newValue = parseFloat(Math.max(0, currentValue - 0.5).toFixed(2));
                handleFieldChange("quantity", newValue);
              }}
            />
          </div>
        )}
      </td>

      {/* RT Hours Column */}
      <td className="px-[1px] md:px-1 py-[2px] caret-black">
        {!cols.rtHours ? (
          // Empty cell for types that don't use RT hours
          <div className="text-gray-400 py-[2px] text-center">-</div>
        ) : readOnly ? (
          <div className="text-gray-700 py-[2px] text-right">
            {item.rt_hours || "0"}
          </div>
        ) : (
          <div className="flex items-center">
            <input
              type="number"
              inputMode="decimal"
              value={item.rt_hours || ""}
              onChange={(e) => handleFieldChange("rt_hours", e.target.value)}
              onFocus={rtHoursInput.handleFocus}
              onKeyDown={(e) =>
                rtHoursInput.handleKeyDown(e, item.rt_hours, (newValue) =>
                  handleFieldChange("rt_hours", newValue)
                )
              }
              onBlur={rtHoursInput.handleBlur}
              className={`h-[24px] w-full text-right pl-1 md:pl-[1px] pr-2 md:pr-2 border border-gray-300 rounded-l-md rounded-r-none focus:outline-none focus:ring-1 focus:ring-blue-500 ${rtHoursInput.isPristine ? "input-pristine" : ""}`}
              min="0"
              step="any"
            />
            <ArrowButtons
              onIncrement={() => {
                const currentValue = parseFloat(item.rt_hours) || 0;
                const newValue = parseFloat((currentValue + 0.5).toFixed(2));
                handleFieldChange("rt_hours", newValue);
              }}
              onDecrement={() => {
                const currentValue = parseFloat(item.rt_hours) || 0;
                const newValue = parseFloat(Math.max(0, currentValue - 0.5).toFixed(2));
                handleFieldChange("rt_hours", newValue);
              }}
            />
          </div>
        )}
      </td>

      {/* OT Hours Column */}
      <td className="px-[1px] md:px-1 py-[2px] caret-black">
        {!cols.otHours ? (
          // Empty cell for types that don't use OT hours
          <div className="text-gray-400 py-[2px] text-center">-</div>
        ) : readOnly ? (
          <div className="text-gray-700 py-[2px] text-right">
            {item.ot_hours || "0"}
          </div>
        ) : (
          <div className="flex items-center">
            <input
              type="number"
              inputMode="decimal"
              value={item.ot_hours || ""}
              onChange={(e) => handleFieldChange("ot_hours", e.target.value)}
              onFocus={otHoursInput.handleFocus}
              onKeyDown={(e) =>
                otHoursInput.handleKeyDown(e, item.ot_hours, (newValue) =>
                  handleFieldChange("ot_hours", newValue)
                )
              }
              onBlur={otHoursInput.handleBlur}
              className={`h-[24px] w-full text-right pl-1 md:pl-[1px] pr-2 md:pr-2 border border-gray-300 rounded-l-md rounded-r-none focus:outline-none focus:ring-1 focus:ring-blue-500 ${otHoursInput.isPristine ? "input-pristine" : ""}`}
              min="0"
              step="any"
            />
            <ArrowButtons
              onIncrement={() => {
                const currentValue = parseFloat(item.ot_hours) || 0;
                const newValue = parseFloat((currentValue + 0.5).toFixed(2));
                handleFieldChange("ot_hours", newValue);
              }}
              onDecrement={() => {
                const currentValue = parseFloat(item.ot_hours) || 0;
                const newValue = parseFloat(Math.max(0, currentValue - 0.5).toFixed(2));
                handleFieldChange("ot_hours", newValue);
              }}
            />
          </div>
        )}
      </td>

      {/* Unit Column */}
      <td className="py-[2px] caret-black text-right">
        {readOnly ||
          (!typeConfig.isCustom && item.type !== "material") ||
          units.length <= 1 ? (
          <div className="text-gray-700 mr-1 py-[2px] text-right">
            {typeConfig.defaultUnit}
          </div>
        ) : (
          <select
            value={item.unit || typeConfig.defaultUnit}
            onChange={(e) => handleFieldChange("unit", e.target.value)}
            className="w-full h-[24px] px-1 py-0 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-right"
          >
            {units.map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
        )}
      </td>

      {/* Price/Unit (RT) Column - Only show when showPricing is true */}
      {showPricing && (
        <td className="px-[1px] md:px-1 py-[2px] caret-black">
          {readOnly ? (
            <div className="text-gray-700 py-[2px] text-right">
              ${parseFloat(item.rate_rt || 0).toFixed(2)}
            </div>
          ) : (
            <div className="relative group">
              <div className="flex items-center">
                <input
                  type="number"
                  inputMode="decimal"
                  value={item.rate_rt ?? ""}
                  onChange={(e) => handleFieldChange("rate_rt", e.target.value)}
                  onFocus={rateRtInput.handleFocus}
                  onKeyDown={(e) =>
                    rateRtInput.handleKeyDown(e, item.rate_rt, (newValue) =>
                      handleFieldChange("rate_rt", newValue)
                    )
                  }
                  onBlur={rateRtInput.handleBlur}
                  placeholder={!loadedFromReport ? "0" : "N/A"}
                  className={`w-full h-[24px] pl-1 md:pl-[1px] pr-2 md:pr-2 py-0 border border-gray-300 rounded-l-md rounded-r-none focus:outline-none focus:ring-1 focus:ring-blue-500 text-right ${rateRtInput.isPristine ? "input-pristine" : ""}`}
                  min="0"
                  step="any"
                />
                <ArrowButtons
                  onIncrement={() => {
                    const currentValue = parseFloat(item.rate_rt) || 0;
                    const newValue = parseFloat((currentValue + 0.5).toFixed(2));
                    handleFieldChange("rate_rt", newValue);
                  }}
                  onDecrement={() => {
                    const currentValue = parseFloat(item.rate_rt) || 0;
                    const newValue = parseFloat(Math.max(0, currentValue - 0.5).toFixed(2));
                    handleFieldChange("rate_rt", newValue);
                  }}
                />
              </div>
              {(item.rate_rt === null ||
                item.rate_rt === undefined ||
                item.rate_rt === "") &&
                loadedFromReport && (
                  <TooltipArrow
                    text="Pricing not set in"
                    position="above"
                    linkText="Admin Panel"
                    onLinkClick={() => {
                      window.open(
                        `${ADMIN_LINKS[item.type]}/${item.option_id}/change/`,
                        "_blank"
                      );
                    }}
                  />
                )}
            </div>
          )}
        </td>
      )}

      {/* Price/Unit (OT) Column - Only show when showPricing is true */}
      {showPricing && (
        <td className="px-[1px] md:px-1 py-[2px] caret-black">
          {!cols.priceOT ? (
            // Empty cell for types that don't use OT pricing
            <div className="text-gray-400 py-[2px] text-center">-</div>
          ) : readOnly ? (
            <div className="text-gray-700 py-[2px] text-right">
              ${parseFloat(item.rate_ot || 0).toFixed(2)}
            </div>
          ) : (
            <div className="relative group">
              <div className="flex items-center">
                <input
                  type="number"
                  inputMode="decimal"
                  value={item.rate_ot ?? ""}
                  onChange={(e) => handleFieldChange("rate_ot", e.target.value)}
                  onFocus={rateOtInput.handleFocus}
                  onKeyDown={(e) =>
                    rateOtInput.handleKeyDown(e, item.rate_ot, (newValue) =>
                      handleFieldChange("rate_ot", newValue)
                    )
                  }
                  onBlur={rateOtInput.handleBlur}
                  placeholder={!loadedFromReport ? "0" : "N/A"}
                  className={`w-full h-[24px] pl-1 md:pl-[1px] pr-2 md:pr-2 py-0 border border-gray-300 rounded-l-md rounded-r-none focus:outline-none focus:ring-1 focus:ring-blue-500 text-right ${rateOtInput.isPristine ? "input-pristine" : ""}`}
                  min="0"
                  step="any"
                />
                <ArrowButtons
                  onIncrement={() => {
                    const currentValue = parseFloat(item.rate_ot) || 0;
                    const newValue = parseFloat((currentValue + 0.5).toFixed(2));
                    handleFieldChange("rate_ot", newValue);
                  }}
                  onDecrement={() => {
                    const currentValue = parseFloat(item.rate_ot) || 0;
                    const newValue = parseFloat(Math.max(0, currentValue - 0.5).toFixed(2));
                    handleFieldChange("rate_ot", newValue);
                  }}
                />
              </div>
              {(item.rate_ot === null ||
                item.rate_ot === undefined ||
                item.rate_ot === "") &&
                loadedFromReport && (
                  <TooltipArrow
                    text="OT Pricing not set in"
                    position="above"
                    linkText="Admin Panel"
                    onLinkClick={() => {
                      window.open(
                        `${ADMIN_LINKS[item.type]}/${item.option_id}/change/`,
                        "_blank"
                      );
                    }}
                  />
                )}
            </div>
          )}
        </td>
      )}

      {/* Total Column - Only show when showPricing is true */}
      {showPricing && (
        <td className="px-[1px] md:px-1 py-[2px] caret-black">
          <div className="text-gray-700 font-medium text-right">
            ${total.toFixed(2)}
          </div>
        </td>
      )}

      {/* Actions Column - Hidden when readOnly */}
      {!readOnly && (
        <td className="px-[1px] md:px-1 py-[2px] caret-black">
          <div className="flex gap-0 md:gap-1 justify-end">
            <Button
              variant="custom"
              icon={<ClipboardCopy className="w-4 h-4 text-blue-600" />}
              onClick={() => onDuplicate?.(item)}
              className="p-1 hover:bg-blue-100"
              title="Duplicate"
            />
            <Button
              variant="custom"
              icon={<Trash className="w-4 h-4 text-red-600" />}
              onClick={() => onRemove?.(item)}
              className="p-1 pr-2 hover:bg-red-100"
              title="Remove"
            />
          </div>
        </td>
      )}
    </tr>
  );
};

export default TMLineItem;
